import { Position, Positions } from '@agilox/ui-common';
import {
	AfterViewInit,
	ApplicationRef,
	ComponentRef,
	contentChild,
	createComponent,
	Directive,
	EmbeddedViewRef,
	EnvironmentInjector,
	HostListener,
	inject,
	Injector,
	input,
	model,
	OnDestroy,
	output,
	signal,
	Renderer2,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, Subscription } from 'rxjs';
import { DropdownComponent } from '../component/dropdown/dropdown.component';
import { DropdownContentDirective } from './dropdown-content.directive';
import { DropdownTriggerDirective } from './dropdown-trigger.directive';

const DROPDOWN_CLOSE_ANIMATION_DELAY = 300;

@Directive({
	selector: '[uiDropdown]',
	standalone: false,
})
export class DropdownDirective implements AfterViewInit, OnDestroy {
	private applicationRef = inject(ApplicationRef);
	private environmentInjector = inject(EnvironmentInjector);
	private injector = inject(Injector);
	private renderer = inject(Renderer2);

	ignoreTrigger = input<boolean>(false);
	id = input<string>('');
	openDropdownToTheLeft = input<boolean>(false);
	containerSelector = input<string>('.app');
	useContainerWidth = input<boolean>(false);
	parent = input<string>('');

	fullWidth = model<boolean>(false);
	dropdownPosition = model<Position>(Positions.Bottom);

	isOpen = signal(false);
	openChange = output<boolean>();

	dropdownTrigger = contentChild<DropdownTriggerDirective | null>(DropdownTriggerDirective);
	dropdownContent = contentChild<DropdownContentDirective | null>(DropdownContentDirective);

	private _dropdownRef: ComponentRef<DropdownComponent> | null = null;
	private _dropdownOpen = signal<boolean>(false);
	private _dropdownOpen$ = toObservable(this._dropdownOpen);

	private clickListenerSubscription: Subscription | null = null;

	private unlistenScroll: (() => void) | null = null;

	private setOpen(open: boolean): void {
		if (open === this._dropdownOpen()) {
			return;
		}
		this._dropdownOpen.set(open);

		this.isOpen.set(open);
		this.openChange.emit(open);
		if (open) {
			this.initDropdown();
			this.listenToScroll();
		} else {
			this.destroyDropdown();
			this.stopListeningToScroll();
		}
	}

	ngAfterViewInit() {
		if (this.dropdownTrigger()) {
			this.dropdownTrigger()!.ignoreTrigger = this.ignoreTrigger;
			this.dropdownTrigger()!.toggleRequest.set(() => this.toggleDropdown());
		}
	}

	private listenToScroll() {
		this.stopListeningToScroll();
		this.unlistenScroll = this.renderer.listen(
			'window',
			'scroll',
			() => {
				this.setPosition();
				if (this._dropdownRef) {
					this._dropdownRef.changeDetectorRef.detectChanges();
				}
			},
			{
				capture: true,
			}
		);
	}

	private stopListeningToScroll() {
		if (this.unlistenScroll) {
			this.unlistenScroll();
			this.unlistenScroll = null;
		}
	}

	initDropdown() {
		if (this.dropdownPosition() === Positions.Left || this.dropdownPosition() === Positions.Right) {
			this.fullWidth.set(true);
		}
		this._dropdownRef = createComponent(DropdownComponent, {
			environmentInjector: this.environmentInjector,
			elementInjector: this.injector,
		});
		this.applicationRef.attachView(this._dropdownRef.hostView);
		const [domElem] = (this._dropdownRef.hostView as EmbeddedViewRef<any>).rootNodes;
		this.setProperties();

		const parentElement = this.parent() ? document.querySelector(this.parent()) : null;
		if (parentElement) {
			parentElement.appendChild(domElem);
		} else {
			document.body.appendChild(domElem);
		}

		if (this.dropdownTrigger()?.isOpenWithKeyboard) {
			this.initDropdownFocus();
		}
	}

	setProperties() {
		if (this._dropdownRef && this.dropdownContent()) {
			this._dropdownRef.instance.containerSelector.set(this.containerSelector());
			this.setPosition();
			this._dropdownRef.instance.template.set(this.dropdownContent()!.template);
			this._dropdownRef.instance.id.set(this.id());
		}
	}

	setPosition() {
		if (this._dropdownRef && this.dropdownTrigger()) {
			const { left, bottom, top, right, width } =
				this.dropdownTrigger()!.elementRef.nativeElement.getBoundingClientRect();

			if (this.useContainerWidth() && this.containerSelector()) {
				const appElement = document.querySelector(this.containerSelector());

				this._dropdownRef.instance.width.set(
					appElement ? appElement.getBoundingClientRect().width : window.innerWidth
				);
			} else if (!this.fullWidth()) {
				this._dropdownRef.instance.width.set(width);
			}

			this._dropdownRef.instance.minWidth?.set(width);

			this._dropdownRef.instance.triggerTop.set(top);
			this._dropdownRef.instance.triggerRight.set(right);
			this._dropdownRef.instance.triggerLeft.set(left);
			this._dropdownRef.instance.triggerBottom.set(bottom);
			this._dropdownRef.instance.triggerWidth.set(width);

			this._dropdownRef.instance.openDropdownToTheLeft.set(this.openDropdownToTheLeft());
			this._dropdownRef.instance.dropdownPosition.set(this.dropdownPosition());
		}
	}

	@HostListener('window:resize')
	onResize() {
		this.setPosition();
		if (this._dropdownRef) {
			this._dropdownRef.changeDetectorRef.detectChanges();
		}
	}

	public destroyDropdown() {
		if (this._dropdownRef) {
			this._dropdownRef.instance.close();
			setTimeout(() => {
				if (this._dropdownRef) {
					this.applicationRef.detachView(this._dropdownRef.hostView);
					this._dropdownRef.destroy();
					this._dropdownRef = null;
				}
			}, DROPDOWN_CLOSE_ANIMATION_DELAY);
		}
	}

	@HostListener('document:click', ['$event'])
	clickOut(event: MouseEvent) {
		if (!this._dropdownOpen()) {
			return;
		}

		const path = event.composedPath();
		const dropdownContent = this._dropdownRef?.instance.dropdown()?.nativeElement;
		const triggerElement = this.dropdownTrigger()?.elementRef.nativeElement;

		// Check if click is inside this dropdown or its trigger
		const isInsideThisDropdown = dropdownContent && path.includes(dropdownContent);
		const isInsideTrigger = path.includes(triggerElement);

		// Check if click is on any dropdown element (handles nested dropdowns)
		const isInsideAnyDropdown = path.some(
			(el: any) => el.getAttribute?.('data-dropdown') === 'true'
		);

		const isOutsideTrigger = path.some(
			(el: any) => el.getAttribute?.('data-dropdown-trigger') === 'true'
		);

		// Close only if click is outside this dropdown AND not on any dropdown element
		if (!isInsideThisDropdown && !isInsideTrigger && !isInsideAnyDropdown && !isOutsideTrigger) {
			this.closeDropdown();
		}
	}

	@HostListener('document:keydown.escape')
	onEscape() {
		this.setOpen(false);
	}

	toggleDropdown(): void {
		this.setOpen(!this._dropdownOpen());
	}

	openDropdown(): void {
		this.setOpen(true);
	}

	closeDropdown(): void {
		this.dropdownTrigger()?.isOpenWithKeyboard.set(false);
		this.setOpen(false);
	}

	getState(): Observable<boolean> {
		return this._dropdownOpen$;
	}

	ngOnDestroy(): void {
		this.destroyDropdown();
		this.clickListenerSubscription?.unsubscribe();
		this.stopListeningToScroll();
	}

	initDropdownFocus() {
		setTimeout(() => {
			const focusableChildren = this._dropdownRef?.instance
				.dropdown()
				?.nativeElement.querySelectorAll(this.FOCUSABLE_SELECTORS);

			if (!focusableChildren || focusableChildren.length === 0) return;

			let buttonChild = undefined;
			if (this.dropdownTrigger()?.elementRef.nativeElement.tagName.toLowerCase() === 'ui-button') {
				buttonChild = this.dropdownTrigger()?.elementRef.nativeElement.querySelector('button');
			}

			const firstChild = focusableChildren[0];

			if (firstChild) {
				firstChild.focus();

				firstChild.addEventListener('keydown', (e: KeyboardEvent) => {
					if (e.key === 'Tab') {
						if (e.shiftKey) {
							e.preventDefault();

							if (buttonChild) {
								buttonChild.focus();
							} else {
								this.dropdownTrigger()?.elementRef.nativeElement.focus();
							}
							this.closeDropdown();
						}
					}
				});
			}

			const lastChild = focusableChildren[focusableChildren.length - 1];
			if (lastChild) {
				lastChild.addEventListener('keydown', (e: KeyboardEvent) => {
					if (e.key === 'Tab') {
						if (!e.shiftKey) {
							e.preventDefault();

							if (buttonChild) {
								buttonChild.focus();
							} else {
								this.dropdownTrigger()?.elementRef.nativeElement.focus();
							}
							this.closeDropdown();
						}
					}
				});
			}
		});
	}

	private readonly FOCUSABLE_SELECTORS = [
		'a[href]',
		'button:not([disabled])',
		'input:not([disabled]):not([type="hidden"])',
		'select:not([disabled])',
		'textarea:not([disabled])',
		'[tabindex]:not([tabindex="-1"])',
	].join(',');
}
